import { useState } from 'react';
import { Link } from 'react-router-dom';
import { humanizeToolName } from './tool-presentation';
import type { ThreadGate } from './thread';
import { MarkdownPreview } from '../ui/MarkdownPreview';

export interface GateDecision {
  gate: ThreadGate;
  approve: boolean;
  reason?: string;
}

export function HumanGateCard({ gate, canAct = true, onDecide }: { gate: ThreadGate; canAct?: boolean; onDecide: (decision: GateDecision) => void }) {
  const [reason, setReason] = useState('');
  const [deciding, setDeciding] = useState(false);
  const data = gate.data;
  const toolKey = typeof data.toolKey === 'string' ? data.toolKey : 'tool';
  const title = gate.gateKind === 'approval' ? `Approve tool: ${humanizeToolName(toolKey)}` : 'Approve outcome';
  const blocked = gate.gateKind === 'review' && data.blockedOutcome === true;
  const summary = gate.gateKind === 'approval'
    ? (data.toolInput && typeof data.toolInput === 'object' ? JSON.stringify(data.toolInput, null, 2) : '')
    : (typeof data.outputSummary === 'string' ? data.outputSummary : typeof data.output === 'string' ? data.output : '');

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40" data-testid={`gate-card-${gate.id}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="font-semibold">{title}</span>
        <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-800">{gate.status === 'pending' ? 'awaiting decision' : gate.status}</span>
        {gate.gateKind === 'review' ? (
          <Link className="ml-auto text-xs text-blue-600" to="/review">Full evidence</Link>
        ) : (
          <Link className="ml-auto text-xs text-blue-600" to="/approvals">Approvals</Link>
        )}
      </div>
      {summary && <div className="mt-2 max-h-60 overflow-auto text-xs"><MarkdownPreview content={summary} /></div>}
      {gate.status === 'pending' && canAct ? (
        <>
          <textarea className="mt-2 w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-900" rows={2} placeholder={gate.gateKind === 'review' ? 'Feedback for the agent (required when rejecting)...' : 'Add a note about your decision...'} value={reason} onChange={(event) => setReason(event.target.value)} />
          <div className="mt-2 flex gap-2">
            <button type="button" className="flex-1 rounded bg-green-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" disabled={deciding} data-testid="gate-approve" onClick={() => { setDeciding(true); onDecide({ gate, approve: true, reason: reason.trim() || undefined }); }}>{blocked ? 'Retry' : 'Approve'}</button>
            <button type="button" className="flex-1 rounded bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" disabled={deciding} data-testid="gate-reject" onClick={() => { setDeciding(true); onDecide({ gate, approve: false, reason: reason.trim() || undefined }); }}>{gate.gateKind === 'review' || blocked ? 'Request changes' : 'Reject'}</button>
          </div>
        </>
      ) : gate.status === 'pending' ? (
        <p className="mt-2 text-xs text-gray-500">Waiting for an operator decision. You have read-only access.</p>
      ) : typeof data.reason === 'string' && data.reason.trim() ? (
        <p className="mt-2 text-xs text-gray-500">"{data.reason}"</p>
      ) : null}
    </div>
  );
}
